// AdditionalQueryAdvancedPJCompact.jsx — layout corporativo/compacto com grupos e dependentes
import {
  Checkbox,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Toast,
} from "flowbite-react";
import { useQuery } from "react-query";
import { QUERIES } from "../../../config/apiConfig";
import axios from "../../../config/axiosInstance";
import { useMemo, useState } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { HiExclamationCircle } from "react-icons/hi";

// === Definição dos grupos principais + seus dependentes (mesmas URLs que você enviou)
const groupDefinitions = {
  "additional-avalia-max-plus-pj": {
    label: "Avalia Max Plus PJ",
    description: "Pacote amplo para enriquecer a análise PJ.",
    dependents: [
      {
        url: "additional-max-faturamento-presumido",
        label: "Faturamento Presumido",
      },
      { url: "additional-max-quadro-social-plus", label: "Quadro Social Plus" },
      { url: "additional-max-limite-credito", label: "Limite de Crédito" },
      {
        url: "additional-max-participacoes-empresas",
        label: "Participações em Empresas",
      },
      {
        url: "additional-max-pagamentos-fornecedores",
        label: "Pagamentos a Fornecedores",
      },
      {
        url: "additional-avalia-protesto-nacional-avancado-pj",
        label: "Protesto Nacional",
      },
    ],
  },
  "additional-avalia-risco-completo-positivo-pj": {
    label: "Avalia Risco Completo Positivo PJ",
    description: "Foco em risco, restrições e protestos com positivo.",
    dependents: [
      {
        url: "additional-risco-faturamento-presumido",
        label: "Faturamento Presumido",
      },
      {
        url: "additional-risco-quadro-social-plus",
        label: "Quadro Social Plus",
      },
      { url: "additional-risco-limite-credito", label: "Limite de Crédito" },
      {
        url: "additional-risco-participacoes-empresas",
        label: "Participações em Empresas",
      },
      {
        url: "additional-risco-pagamentos-fornecedores",
        label: "Pagamentos a Fornecedores",
      },
      {
        url: "additional-avalia-protesto-nacional-avancado-pj",
        label: "Protesto Nacional",
      },
    ],
  },
};

const RISCO_COMPLETO_PJ = "additional-avalia-risco-completo-positivo-pj";
const PROTESTO_AVANCADO_PJ = "additional-avalia-protesto-nacional-avancado-pj";

// Item compacto (checkbox à esquerda, label à direita)
function LineCheck({ id, label, checked, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={checked}
        onChange={onChange}
        className="scale-90"
      />
      <Label htmlFor={id} className="text-[13px] font-medium text-slate-800">
        {label}
      </Label>
    </div>
  );
}

export default function AdditionalQueryAdvancedPJ({
  selectedQueries,
  setSelectedQueries,
  type = "J",
}) {
  const [openModal, setOpenModal] = useState(false);
  const [warning, setWarning] = useState("");

  // Busca o que a empresa realmente tem disponível
  const { data: queryAdditionals = [] } = useQuery(
    ["queryAdditionals", type],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_ADDITIONALS_BY_COMPANY}?type=${type}`
      );
      return data.data;
    }
  );

  const availableUrls = useMemo(
    () => new Set(queryAdditionals.map((q) => q.url)),
    [queryAdditionals]
  );

  const hasAvailableGroups = useMemo(
    () => Object.keys(groupDefinitions).some((g) => availableUrls.has(g)),
    [availableUrls]
  );

  // Setter seguro: dedupe + só mantém URLs realmente disponíveis
  const safeSetSelected = (updater) => {
    setSelectedQueries((prev) => {
      const next = updater(prev);
      const filtered = next.filter((u) => availableUrls.has(u));
      return Array.from(new Set(filtered));
    });
  };

  const handleCheckboxChange = (queryId, isChecked, dependencyId = null) => {
    // Bloqueia dependente sem o principal
    if (dependencyId && !selectedQueries.includes(dependencyId)) {
      setWarning("Você precisa escolher a opção principal antes.");
      setTimeout(() => setWarning(""), 3500);
      return;
    }

    safeSetSelected((prev) => {
      let updated = isChecked
        ? [...prev, queryId]
        : prev.filter((id) => id !== queryId);

      // Se desmarcar um grupo principal, remove todos os seus dependentes
      if (!isChecked && groupDefinitions[queryId]) {
        const deps =
          groupDefinitions[queryId].dependents?.map((d) => d.url) || [];
        updated = updated.filter((id) => !deps.includes(id));
      }

      // Regra automática: marcar Risco Completo => inclui Protesto Avançado
      if (
        queryId === RISCO_COMPLETO_PJ &&
        isChecked &&
        availableUrls.has(PROTESTO_AVANCADO_PJ)
      ) {
        if (!updated.includes(PROTESTO_AVANCADO_PJ)) {
          updated.push(PROTESTO_AVANCADO_PJ);
        }
      }
      // Desmarcar Risco Completo => remove Protesto Avançado
      if (queryId === RISCO_COMPLETO_PJ && !isChecked) {
        updated = updated.filter((id) => id !== PROTESTO_AVANCADO_PJ);
      }

      return updated;
    });
  };

  if (!queryAdditionals.length || !hasAvailableGroups) return null;

  return (
    <>
      {warning && (
        <div className="fixed bottom-5 right-5 z-50">
          <Toast className="bg-yellow-100 text-yellow-800 shadow-lg rounded-lg">
            <HiExclamationCircle className="h-5 w-5 text-yellow-600" />
            <div className="text-sm font-medium ml-2">{warning}</div>
          </Toast>
        </div>
      )}

      {/* Cabeçalho enxuto — mesmo “look” do layout corporativo */}
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-900">
          Adicionais de Consulta — PJ
        </h3>
        <AiOutlineQuestionCircle
          className="h-4 w-4 cursor-pointer text-slate-500 hover:text-slate-700"
          onClick={() => setOpenModal(true)}
          title="Como funcionam os adicionais"
        />
      </div>
      <p className="mb-4 text-[12px] text-slate-600">
        Selecione os módulos necessários. Interface compacta, sem distrações.
      </p>

      {/* Grid em 2 colunas — cada coluna é um GRUPO (Max Plus / Risco Completo) */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {Object.entries(groupDefinitions).map(([mainUrl, def]) => {
          if (!availableUrls.has(mainUrl)) return null;
          const isMainSelected = selectedQueries.includes(mainUrl);

          return (
            <section
              key={mainUrl}
              className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-md"
            >
              {/* Cabeçalho do grupo (checkbox principal + título + descrição curta) */}
              <div className="mb-2 flex items-start gap-2">
                <Checkbox
                  id={mainUrl}
                  checked={isMainSelected}
                  onChange={(e) =>
                    handleCheckboxChange(mainUrl, e.target.checked)
                  }
                  className="scale-95"
                />
                <div className="flex-1 -mt-1">
                  <Label
                    htmlFor={mainUrl}
                    className="text-[14px] font-semibold text-slate-900"
                  >
                    {def.label}
                  </Label>
                  {def.description && (
                    <div className="text-[11px] text-slate-500">
                      {def.description}
                    </div>
                  )}
                </div>
              </div>

              {/* Lista densa dos dependentes — 2 colunas no desktop */}
              <div className="grid grid-cols-1 gap-y-1 sm:grid-cols-2">
                {def.dependents
                  .filter((d) => availableUrls.has(d.url))
                  .map((d) => {
                    const depChecked = selectedQueries.includes(d.url);
                    return (
                      <LineCheck
                        key={`${mainUrl}-${d.url}`}
                        id={d.url}
                        label={d.label}
                        checked={depChecked}
                        onChange={(e) =>
                          handleCheckboxChange(d.url, e.target.checked, mainUrl)
                        }
                      />
                    );
                  })}
              </div>
            </section>
          );
        })}
      </div>

      {/* Modal de ajuda, discreto */}
      <Modal show={openModal} onClose={() => setOpenModal(false)}>
        <ModalHeader>Adicionais — Como usar</ModalHeader>
        <ModalBody>
          <div className="space-y-2 text-sm text-slate-700">
            <p>
              Marque apenas o que agrega valor à sua análise. Os pacotes{" "}
              <strong>Avalia Max Plus</strong> e{" "}
              <strong>Risco Completo Positivo</strong> permitem combinar insumos
              rapidamente.
            </p>
            <p>
              Ao ativar <strong>Risco Completo</strong>, o{" "}
              <em>Protesto Nacional (Avançado)</em> é incluído automaticamente
              para garantir cobertura mínima.
            </p>
            <p>
              Os dependentes ficam listados em layout compacto, em duas colunas,
              priorizando velocidade e clareza.
            </p>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
